import { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../utils/axios';
import { useGet } from './useHomeData';

export interface StockRow {
  Id: number; Ticker: string; Company: string | null; Sector: string | null;
  Industry: string | null; Country: string | null; BlackList: boolean;
}
export interface StocksResponse {
  stocks: StockRow[]; total: number; skip: number; limit: number;
  page: number; total_pages: number; sectors: string[]; industries: string[];
}
export interface StockTransaction {
  Id: number; Time: string; Ticker: string; Action: string; Price: number;
  Investment: number; ProfitAndLossPercent: number | null; ProfitAndLoss: number | null;
  TraderId: number; TraderName: string;
}
export interface PerTraderSummary { trader_id: number; name: string; trades: number; pnl: number; }
export interface StockDetailResponse {
  stock: StockRow;
  transactions: StockTransaction[];
  summary: { total_trades: number; total_pnl: number; per_trader: PerTraderSummary[] };
}
export interface PricePoint { date: string; close: number; }
export type PriceRange = '1d' | '1w' | '1m' | '6m' | '1y' | '5y';
export interface StockPricesResponse { ticker: string; range: string; interval: string; data: PricePoint[]; }
export interface StockProfile {
  ticker: string; available: boolean;
  description: string | null; website: string | null; employees: number | null;
  market_cap: number | null; trailing_pe: number | null; forward_pe: number | null;
  beta: number | null; dividend_yield: number | null; avg_volume: number | null;
  high_52w: number | null; low_52w: number | null; current_price: number | null;
  pct_off_high: number | null; short_pct_of_float: number | null;
  recommendation: string | null; target_mean_price: number | null; analyst_count: number | null;
  next_earnings_date: string | null;
}
export interface NewsItem { title: string; link: string; publisher: string | null; published_at: string | null; summary: string | null; }

export const useStockDetail = (ticker: string) =>
  useGet<StockDetailResponse>(`/stocks/${encodeURIComponent(ticker)}`);
export const useStockPrices = (ticker: string, range: PriceRange = '1y') =>
  useGet<StockPricesResponse>(`/stocks/${encodeURIComponent(ticker)}/prices?range=${range}`);
export const useStockProfile = (ticker: string) =>
  useGet<StockProfile>(`/stocks/${encodeURIComponent(ticker)}/profile`);
export const useStockNews = (ticker: string) =>
  useGet<{ ticker: string; items: NewsItem[] }>(`/stocks/${encodeURIComponent(ticker)}/news`);

export interface VoterScore { name: string; family: 'trend' | 'reversion'; score: number; weight: number; }
export interface IndicatorScore {
  score: number; trend_score: number; reversion_score: number; blend_weight: number;
  veto_buy: boolean; veto_sell: boolean;
  buy_veto_reasons: string[]; sell_veto_reasons: string[]; explanation: string;
  // Optional: a stale server without the per-voter breakdown must not break the page.
  voters?: VoterScore[];
  // Optional: the ADX reading behind blend_weight; null when the engine had no regime read.
  adx?: number | null;
}
export interface IndicatorChart { name: string; image: string; }
export interface StockIndicatorsResponse {
  ticker: string; available: boolean; generated_at?: string;
  score: IndicatorScore | null; charts: IndicatorChart[];
}

// ---- Indicators prefetch ----------------------------------------------------
// The indicators endpoint is expensive (~5s cold: yfinance 1y fetch + 10
// matplotlib renders) and has no server-side single-flight, so the detail page
// pre-warms it and the indicators page JOINS the same in-flight promise instead
// of issuing a second request (which would double the server work under the
// render lock and re-download the ~1.6 MB payload).
//
// Reads do not consume entries: React 18 StrictMode double-mounts effects in
// dev, and a one-shot entry would make the second mount fire a duplicate
// request. Staleness is bounded by a TTL, memory by a small cap (payloads are
// ~1.6 MB each), and refetch() drops the entry so retries always hit the network.
interface IndicatorsPrefetchEntry { promise: Promise<StockIndicatorsResponse>; fetchedAt: number; }
const indicatorsPrefetch = new Map<string, IndicatorsPrefetchEntry>();
const PREFETCH_TTL_MS = 10 * 60 * 1000;
const PREFETCH_MAX_ENTRIES = 5;

const indicatorsUrl = (ticker: string) => `/stocks/${encodeURIComponent(ticker)}/indicators`;

/** Test support: the module-level map outlives each test's render. */
export const clearIndicatorsPrefetch = (): void => { indicatorsPrefetch.clear(); };

const evictIfFull = (ticker: string) => {
  if (!indicatorsPrefetch.has(ticker) && indicatorsPrefetch.size >= PREFETCH_MAX_ENTRIES) {
    // Map preserves insertion order and (re)publishes delete-then-set, so the
    // first key is always the oldest fetch.
    const oldest = indicatorsPrefetch.keys().next().value;
    if (oldest !== undefined) indicatorsPrefetch.delete(oldest);
  }
};

export const prefetchStockIndicators = (ticker: string): void => {
  if (!ticker) return;
  const existing = indicatorsPrefetch.get(ticker);
  if (existing && Date.now() - existing.fetchedAt < PREFETCH_TTL_MS) return;
  evictIfFull(ticker);
  const promise = axios.get<StockIndicatorsResponse>(indicatorsUrl(ticker)).then((r) => r.data);
  // Failures are never cached (server convention); the guard keeps a newer
  // in-flight prefetch from being deleted by an older one's late rejection.
  promise.catch(() => {
    if (indicatorsPrefetch.get(ticker)?.promise === promise) indicatorsPrefetch.delete(ticker);
  });
  indicatorsPrefetch.delete(ticker);  // refresh insertion order for eviction
  indicatorsPrefetch.set(ticker, { promise, fetchedAt: Date.now() });
};

const getPrefetchedIndicators = (ticker: string): Promise<StockIndicatorsResponse> | null => {
  const entry = indicatorsPrefetch.get(ticker);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt >= PREFETCH_TTL_MS) {
    indicatorsPrefetch.delete(ticker);
    return null;
  }
  return entry.promise;
};

export const useStockIndicators = (ticker: string) => {
  const [data, setData] = useState<StockIndicatorsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Same monotonic-counter stale-response guard as useGet (useHomeData.ts).
  const fetchSeq = useRef(0);

  const load = useCallback(async (joinPrefetch: boolean) => {
    const seq = ++fetchSeq.current;
    // A malformed route would otherwise GET /stocks//indicators; match the
    // same guard prefetchStockIndicators applies.
    if (!ticker) { setLoading(false); return; }
    try {
      setLoading(true);
      let promise = joinPrefetch ? getPrefetchedIndicators(ticker) : null;
      if (!promise) {
        // Network path (no prefetch, or an explicit retry): drop any stored
        // entry so a later mount can't resurrect the result being replaced.
        indicatorsPrefetch.delete(ticker);
        promise = axios.get<StockIndicatorsResponse>(indicatorsUrl(ticker)).then((r) => r.data);
        if (joinPrefetch) {
          // Publish so a StrictMode remount joins this request instead of
          // duplicating it; refetch deliberately stays unpublished so its
          // result can't be re-joined.
          evictIfFull(ticker);
          indicatorsPrefetch.set(ticker, { promise, fetchedAt: Date.now() });
          promise.catch(() => {
            if (indicatorsPrefetch.get(ticker)?.promise === promise) indicatorsPrefetch.delete(ticker);
          });
        }
      }
      const result = await promise;
      if (seq !== fetchSeq.current) return;  // stale response; a newer request is in flight
      setData(result);
      setError(null);
    } catch (e) {
      if (seq !== fetchSeq.current) return;
      console.error(`Error fetching ${indicatorsUrl(ticker)}:`, e);
      setError('Failed to load data');
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [ticker]);

  const refetch = useCallback(() => load(false), [load]);
  useEffect(() => {
    load(true);
    // Stale-mark in-flight responses on unmount/ticker change (useGet precedent).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: bumping fetchSeq in cleanup stale-marks in-flight responses
    return () => { fetchSeq.current++; };
  }, [load]);
  return { data, loading, error, refetch };
};
